import { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Menu as MenuIcon, Beer, Coffee, Wine, Utensils } from 'lucide-react';

interface MenuItem {
  name: string;
  description: string;
  price: string;
  highlight?: boolean;
}

interface MenuCategory {
  id: string;
  title: string;
  items: MenuItem[];
}

const MenuSection = ({ 
  title, 
  items 
}: { 
  title: string; 
  items: MenuItem[] 
}) => (
  <div className="mb-12">
    <h3 className="heading-sm mb-6 text-barbecue-600">{title}</h3>
    <div className="space-y-1">
      {items.map((item, index) => (
        <div key={index} className="menu-item">
          <div>
            <div className="flex items-center">
              <h4 className={`font-medium ${item.highlight ? 'text-barbecue-600' : ''}`}>
                {item.name}
              </h4>
              {item.highlight && (
                <span className="ml-2 bg-barbecue-100 text-barbecue-800 text-xs px-2 py-0.5 rounded">
                  Priporočamo
                </span>
              )}
            </div>
            <p className="text-sm text-foreground/70 mt-1">{item.description}</p>
          </div>
          <div className="font-medium whitespace-nowrap ml-4">{item.price} €</div>
        </div>
      ))}
    </div>
  </div>
);

const MenuPage = () => {
  const [activeTab, setActiveTab] = useState('food');
  const [activeCategory, setActiveCategory] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Set default active category based on tab
    if (activeTab === 'food') {
      setActiveCategory('starters');
    } else {
      setActiveCategory('beers');
    }
  }, [activeTab]);

  const foodCategories: MenuCategory[] = [
    {
      id: 'starters',
      title: 'Predjedi',
      items: [
        { name: 'Domači kruh z namazi', description: 'Hišni kruh s tremi različnimi namazi', price: '6.50' },
        { name: 'Goveja juha z rezanci', description: 'Tradicionalna slovenska goveja juha', price: '4.50' },
        { name: 'Narezek domačih mesnin', description: 'Izbor lokalnih suhomesnatih izdelkov', price: '9.80', highlight: true },
        { name: 'Popečen kruh z vampi', description: 'Tradicionalna slovenska jed', price: '8.50' },
        { name: 'Zelenjavna kremna juha', description: 'Sezonska zelenjava z drobtinami', price: '4.50' },
      ],
    },
    {
      id: 'bbq',
      title: 'Jedi z žara',
      items: [
        { name: 'Mešano meso z žara', description: 'Čevapčiči, pleskavica, svinjski vrat, piščančji file', price: '15.90', highlight: true },
        { name: 'Pleskavica polnjena s sirom', description: 'Sočna pleskavica z mladim sirom', price: '11.50' },
        { name: 'BBQ rebra', description: 'Marinirana svinjska rebra s prilogo', price: '14.80' },
        { name: 'Piščančji ražnjiči', description: 'Mariniran piščanec z zelenjavo', price: '12.50' },
        { name: 'Čevapčiči', description: '10 kosov s prilogo', price: '9.90' },
        { name: 'T-bone steak', description: '400g, serviran z omako iz zelenega popra', price: '24.90', highlight: true },
        { name: 'Vegi nabodalo', description: 'Nabodalo iz sezonske zelenjave s tofu sirom', price: '10.50' },
      ],
    },
    {
      id: 'mains',
      title: 'Glavne jedi',
      items: [
        { name: 'Telečja obara z ajdovimi žganci', description: 'Tradicionalna slovenska jed', price: '12.90' },
        { name: 'Dunajski zrezek', description: 'S praženim krompirjem in zelenjavo', price: '11.50' },
        { name: 'Pečena postrv', description: 'Sveža postrv s krompirjem in blitvo', price: '15.80', highlight: true },
        { name: 'Jota s klobaso', description: 'Tradicionalna enolončnica s kislim zeljem in fižolom', price: '9.50' },
      ],
    },
    {
      id: 'sides',
      title: 'Priloge',
      items: [
        { name: 'Pražen krompir', description: 'Tradicionalno pripravljen', price: '3.50' },
        { name: 'Ajvar in čebula', description: 'Domač ajvar s svežo čebulo', price: '2.50' },
        { name: 'Mešana solata', description: 'Sveža sezonska zelenjava', price: '3.90' },
        { name: 'Pečena zelenjava', description: 'Sezonska zelenjava, pečena na žaru', price: '4.50' },
        { name: 'Lepinja', description: 'Domača lepinja iz krušne peči', price: '1.50' },
      ],
    },
    {
      id: 'desserts',
      title: 'Sladice',
      items: [
        { name: 'Potica', description: 'Tradicionalna slovenska orehova potica', price: '4.50', highlight: true },
        { name: 'Jabolčni zavitek', description: 'S cimetom in vaniljevo kremo', price: '4.90' },
        { name: 'Čokoladna torta', description: 'Z jagodnim prelivom', price: '5.20' },
        { name: 'Palačinke', description: 'Z domačo marmelado ali nutello', price: '4.50' },
        { name: 'Domači sladoled', description: 'Različni okusi', price: '3.90' },
      ],
    },
  ];

  const drinkCategories: MenuCategory[] = [
    {
      id: 'beers',
      title: 'Pivo',
      items: [
        { name: 'Laško', description: 'Točeno 0.3l', price: '2.70' },
        { name: 'Laško', description: 'Točeno 0.5l', price: '3.20' },
        { name: 'Union', description: 'Točeno 0.3l', price: '2.70' },
        { name: 'Union', description: 'Točeno 0.5l', price: '3.20' },
        { name: 'Lokalno craft pivo', description: 'Steklenica 0.33l', price: '4.50', highlight: true },
        { name: 'Radler', description: 'Steklenica 0.5l', price: '3.30' },
        { name: 'Nealko pivo', description: 'Steklenica 0.5l', price: '3.00' },
      ],
    },
    {
      id: 'wines',
      title: 'Vino',
      items: [
        { name: 'Hiša Malec', description: 'Belo vino - kozarec', price: '3.50' },
        { name: 'Hiša Malec', description: 'Rdeče vino - kozarec', price: '3.50' },
        { name: 'Cviček', description: 'Kozarec', price: '2.80' },
        { name: 'Teran', description: 'Kozarec', price: '3.50' },
        { name: 'Malvazija', description: 'Steklenica 0.75l', price: '18.00' },
        { name: 'Refošk', description: 'Steklenica 0.75l', price: '18.00', highlight: true },
        { name: 'Sauvignon', description: 'Steklenica 0.75l', price: '22.00' },
        { name: 'Merlot', description: 'Steklenica 0.75l', price: '24.00' },
      ],
    },
    {
      id: 'spirits',
      title: 'Žgane pijače',
      items: [
        { name: 'Domači borovničevec', description: '0.03l', price: '2.50', highlight: true },
        { name: 'Viljamovka', description: '0.03l', price: '2.80' },
        { name: 'Brinjevec', description: '0.03l', price: '2.80' },
        { name: 'Jägermeister', description: '0.03l', price: '3.00' },
        { name: 'Stock', description: '0.03l', price: '2.50' },
        { name: 'Whiskey Jack Daniels', description: '0.03l', price: '3.50' },
        { name: 'Gin', description: '0.03l', price: '3.00' },
        { name: 'Vodka', description: '0.03l', price: '3.00' },
      ],
    },
    {
      id: 'soft',
      title: 'Brezalkoholne pijače',
      items: [
        { name: 'Domač ledeni čaj', description: '0.3l', price: '2.80' },
        { name: 'Naravni sok', description: 'Jabolko ali pomaranča 0.3l', price: '2.50' },
        { name: 'Coca-Cola', description: '0.25l', price: '2.20' },
        { name: 'Fanta', description: '0.25l', price: '2.20' },
        { name: 'Sprite', description: '0.25l', price: '2.20' },
        { name: 'Voda Radenska', description: 'Gazirana 0.5l', price: '2.00' },
        { name: 'Voda', description: 'Negazirana 0.5l', price: '2.00' },
      ],
    },
    {
      id: 'hot',
      title: 'Tople pijače',
      items: [
        { name: 'Kava', description: 'Espresso', price: '1.50' },
        { name: 'Kava z mlekom', description: '', price: '1.70' },
        { name: 'Cappuccino', description: '', price: '2.20' },
        { name: 'Bela kava', description: '', price: '2.20' },
        { name: 'Vroča čokolada', description: '', price: '2.80' },
        { name: 'Čaj', description: 'Različni okusi', price: '2.00' },
      ],
    },
  ];

  const getCategories = () => {
    return activeTab === 'food' ? foodCategories : drinkCategories;
  };

  const getCategoryIcon = (id: string) => {
    switch (id) {
      case 'starters':
      case 'bbq':
      case 'mains':
      case 'sides':
      case 'desserts':
        return <Utensils className="w-5 h-5" />;
      case 'beers':
        return <Beer className="w-5 h-5" />;
      case 'wines':
      case 'spirits':
        return <Wine className="w-5 h-5" />;
      case 'soft':
      case 'hot':
        return <Coffee className="w-5 h-5" />;
      default:
        return <MenuIcon className="w-5 h-5" />;
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Banner */}
      <section className="relative pt-20 h-[40vh] min-h-[300px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1544025162-d76694265947?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
            alt="Meni - Žar & Bar"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay"></div>
        </div>
        <div className="container mx-auto px-4 md:px-8 z-10 animate-fade-in">
          <h1 className="heading-xl text-white text-center">Naš meni</h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-container">
        <div className="text-center max-w-3xl mx-auto mb-12 animate-fade-in">
          <h2 className="heading-lg mb-4">Kulinarične specialitete in izbrana pijača</h2>
          <p className="paragraph">
            Odkrijte naše tradicionalne slovenske jedi, pripravljene na žaru po 
            receptih, ki se prenašajo iz roda v rod. Ponudbo dopolnjuje skrbno izbrana 
            kolekcija pijač.
          </p>
        </div>

        {/* Menu Tabs */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              onClick={() => setActiveTab('food')}
              className={`inline-flex items-center px-6 py-3 rounded-l-lg ${
                activeTab === 'food'
                  ? 'bg-barbecue-600 text-white'
                  : 'bg-white text-foreground hover:bg-barbecue-50'
              } transition-colors duration-300`}
            >
              <Utensils className="mr-2 h-5 w-5" />
              Hrana
            </button>
            <button
              onClick={() => setActiveTab('drinks')}
              className={`inline-flex items-center px-6 py-3 rounded-r-lg ${
                activeTab === 'drinks'
                  ? 'bg-barbecue-600 text-white'
                  : 'bg-white text-foreground hover:bg-barbecue-50'
              } transition-colors duration-300`}
            >
              <Beer className="mr-2 h-5 w-5" />
              Pijača
            </button>
          </div>
        </div>

        {/* Category Navigation */}
        <div className="mb-12 overflow-x-auto pb-4">
          <div className="flex justify-center space-x-2 min-w-max">
            {getCategories().map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center px-4 py-2 rounded-full transition-colors ${
                  activeCategory === category.id
                    ? 'bg-barbecue-600 text-white'
                    : 'bg-white text-foreground hover:bg-barbecue-50 border border-earth-200'
                }`}
              >
                <span className="mr-2">{getCategoryIcon(category.id)}</span>
                {category.title}
              </button>
            ))}
          </div>
        </div>

        {/* Menu Content */}
        <div className="bg-white rounded-xl shadow-md p-8 animate-fade-in">
          {getCategories().map((category) => (
            <div
              key={category.id}
              className={activeCategory === category.id ? 'block' : 'hidden'}
            >
              <MenuSection title={category.title} items={category.items} />
            </div>
          ))}
        </div>

        {/* Special Dietary Information */}
        <div className="mt-12 p-6 border border-earth-200 rounded-lg bg-earth-50">
          <h3 className="text-lg font-semibold mb-4">Alergeni in diete</h3>
          <p className="mb-4">
            Večina naših jedi lahko vsebuje alergene. Prosimo, da naše osebje 
            obvestite o morebitnih alergijah ali dietnih zahtevah.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              <span className="text-sm">Vegetarijanske opcije</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              <span className="text-sm">Brezglutenske opcije</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
              <span className="text-sm">Veganske opcije</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
              <span className="text-sm">Naše specialitete</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MenuPage;